import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { DetailSaveButton, detailCta } from '~/ui-rocco/pages/detail-shared';
import { formatProjectValueRange } from '../utils/project-value';

interface ProfessionalHeadingCtasProps {
    /** `light` (default) for white sections, `dark` for the Projects page. */
    theme?: 'light' | 'dark';
}

/**
 * Shared right-hand heading cluster for the desktop detail tabs (Figma
 * 2354:58864): the "Typical project value" stat above the Get in touch / Visit
 * Website / Save CTAs. Everything comes from the shared initial route data, so
 * it renders the same on every tab. `theme="dark"` flips it for the dark
 * Projects page. Desktop only — on mobile the CTAs live in the persistent
 * social-profile header + enquiry island.
 */
export const ProfessionalHeadingCtas = ({
    theme = 'light',
}: ProfessionalHeadingCtasProps = {}) => {
    const professional = useProfessionalInitialRouteData();
    const { showEnquiryCTA } = useEnquiryVisibility(professional);
    const { openEnquiryModal } = useEnquiryModal({
        professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        itemTitle: professional.Title,
        includeBranch: true,
    });
    const enquiryLabel =
        professional.primaryCTA?.primaryCTALabelTitleCase ?? 'Get in touch';
    const valueRange = formatProjectValueRange(professional.PriceRanges);
    const isDark = theme === 'dark';

    return (
        // No top margin: the cluster tops out level with the tab's first block
        // (48px under the sub-nav), so it holds one position across every tab.
        <div className="flex flex-col gap-[1.875rem] md:items-end">
            {/* Reserve the stat's height even when there's no value, so the CTAs
                below keep their spot instead of sliding up. */}
            <div
                className={cn(
                    'flex items-baseline justify-end gap-[0.75rem]',
                    !valueRange && 'invisible',
                )}
            >
                <p
                    className={cn(
                        'm-0 font-sans leading-[1.2] font-medium whitespace-nowrap max-md:text-sm md:text-[0.9375rem] md:tracking-[-0.047rem]',
                        isDark ? 'text-white/60' : 'text-[#767676]',
                    )}
                >
                    Typical project value
                </p>
                <p
                    className={cn(
                        'm-0 font-sans leading-[0.9] font-medium max-md:text-[2.5rem] md:text-[3.125rem] md:tracking-[-0.156rem]',
                        isDark ? 'text-white' : 'text-[#141414]',
                    )}
                >
                    {valueRange || ' '}
                </p>
            </div>

            <div className="flex items-center gap-2 max-md:w-full max-md:flex-wrap">
                {showEnquiryCTA ? (
                    <button
                        type="button"
                        onClick={() =>
                            openEnquiryModal({
                                title: enquiryLabel,
                                ctaLabel: enquiryLabel,
                            })
                        }
                        className={detailCta(
                            isDark ? 'filled-dark' : 'filled-light',
                        )}
                    >
                        {enquiryLabel}
                        <svg
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            className="size-[0.83em]"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M5 12h13M12 5l7 7-7 7" />
                        </svg>
                    </button>
                ) : null}
                {professional.Website ? (
                    <RoccoLink
                        to={professional.Website}
                        newTab
                        className={detailCta(
                            isDark ? 'outlined-dark' : 'outlined-light',
                        )}
                    >
                        Visit Website
                    </RoccoLink>
                ) : null}
                <DetailSaveButton
                    pinId={professional.ID}
                    pinType="ProfessionalPin"
                    theme={theme}
                />
            </div>
        </div>
    );
};
